import { SubmissionCheck } from "./SubmissionCheck";
import { useVerifiedSubmission } from "../lib/useVerifiedSubmission";
import { RichText } from "./RichText";
import { t as msg } from "../lib/i18n";
import { useEffect, useId, useState } from "react";
import { Mail, X, ArrowRight } from "lucide-react";
import { getConfig, type Config } from "./Challenge";
import { Modal } from "./Modal";
import { api } from "../lib/api";
export function Login({
  onClose,
  onSuccess,
  local,
  preferredMethod,
}: {
  onClose: () => void;
  onSuccess: () => void;
  local: boolean;
  preferredMethod?: "email";
}) {
  const [method, setMethod] = useState(preferredMethod);
  const native = location.pathname === "/app/sign-in";
  const [age, setAge] = useState(false),
    [config, setConfig] = useState<Config>();
  const hasProviders = !!(
    config?.features.googleLogin || config?.features.appleLogin
  );
  const emailVisible = method === "email" || (!!config && !hasProviders);
  useEffect(() => {
    getConfig()
      .then(setConfig)
      .catch(() => {
        setError(
          msg("Sign-in methods could not load. Close this dialog and try again."),
        );
      });
  }, []);
  async function social(provider: "google" | "apple") {
    setBusy(true);
    setError("");
    try {
      const r = await api<{ url: string }>(
        `/api/auth/${provider}/start`,
        "POST",
        { ageConfirmed: age, returnTo: location.pathname + location.search },
      );
      location.assign(r.url);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }
  const titleId = useId(),
    [email, setEmail] = useState(""),
    [request, setRequest] = useState<{
      requestId: string;
      pairingCode: string;
    } | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    if (!request) return;
    let running = false;
    const timer = setInterval(async () => {
      if (running) return;
      running = true;
      try {
        const s = await api<{ status: string }>(
          `/api/auth/magic/status?rid=${request.requestId}`,
        );
        if (s.status === "approved") {
          await api("/api/auth/magic/claim", "POST", {
            requestId: request.requestId,
          });
          onSuccess();
        } else if (s.status === "consumed") {
          try {
            await api("/api/me");
            onSuccess();
          } catch {
            setError(
              msg("This link was used on another device. Request a new link to sign in here."),
            );
            clearInterval(timer);
          }
        } else if (s.status === "expired") {
          setError(msg("This link expired. Please request a new one."));
          clearInterval(timer);
        }
      } catch {
      } finally {
        running = false;
      }
    }, 2500);
    return () => clearInterval(timer);
  }, [request, onSuccess]);
  const submission = useVerifiedSubmission(JSON.stringify({ email, emailVisible, returnTo: location.pathname + location.search }), sendEmail);
  async function sendEmail(challenge: string) {
    if (!emailVisible) return;
    setBusy(true);
    setError("");
    try {
      setRequest(
        await api("/api/auth/magic", "POST", {
          email,
          turnstileToken: challenge,
          returnTo: location.pathname + location.search,
        }),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal labelledBy={titleId} onDismiss={onClose}>
      <button
        className="icon-button close"
        aria-label={msg("Close sign-in")}
        onClick={onClose}
      >
        <X />
      </button>
      <div className="round-icon">
        <Mail size={24} />
      </div>
      <p className="eyebrow">{msg("KEEP THE GOOD THINGS CLOSE")}</p>
      <h2 id={titleId}>
        {request ? msg("Check your inbox.") : msg("Your promises, all together.")}
      </h2>
      {request ? (
        <>
          <p>
            <RichText message="We sent a sign-in link to {email}. Open it and confirm to continue." values={{email:<strong dir="auto">{email}</strong>}}/></p>
          <div className="pair-code">
            <span>{msg("Signing in from another device?")}</span>
            <strong>{request.pairingCode}</strong>
            <small>
              {msg("Enter this code only if you choose to approve this tab.")}</small>
          </div>
          <button
            className="text-button"
            onClick={() => {
              setRequest(null);
              setError("");
            }}
          >
            {msg("Use a different email or send again")}</button>
          {local && (
            <a
              className="button secondary full"
              target="_blank"
              rel="noreferrer"
              href="/dev/inbox"
            >
              {msg("Open development inbox")}<ArrowRight size={16} />
            </a>
          )}
        </>
      ) : (
        <form onSubmit={e => { e.preventDefault(); submission.request(); }}>
          <p>
            {native
              ? msg("Sign in to the same account you use on the web. Confirm before continuing to the app.")
              : msg("Sign in to send your card and keep track of your promises. Your draft is saved on this device.")}
          </p>
          {emailVisible && (
            <>
              <label>
                {msg("Email address")}<input
                  disabled={busy}
                  data-autofocus
                  type="email"
                  autoComplete="email"
                  placeholder={"you@example.com"}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <SubmissionCheck action="login" submission={submission} />
              <button className="button full" disabled={busy || !!submission.intent}>
                {busy ? msg("Sending…") : submission.intent ? msg("Verifying…") : msg("Email me a sign-in link")}
                <ArrowRight size={17} />
              </button>
              <small className="muted">
                {msg("No password to remember. Just a little confirmation.")}</small>
            </>
          )}
          {!config && <p role="status">{msg("Loading sign-in methods…")}</p>}
          {!emailVisible && config && hasProviders && (
            <div className="social-login">
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={age}
                  onChange={(e) => setAge(e.target.checked)}
                />
                <span>
                  <RichText message="I am 13 or older, agree to the {terms} and have read the {privacy}. Positive exchanges between accounts create mutual friendships, sharing profile names and enabling direct cards." values={{terms:<a href="/terms">{msg("Terms")}</a>,privacy:<a href="/privacy">{msg("Privacy notice")}</a>}}/></span>
              </label>
              <p className="sign-in-help">
                {msg("Choose a provider to open its secure authorization page.")}</p>
              {config.features.googleLogin && (
                <button
                  type="button"
                  className="button secondary full"
                  disabled={busy || !age}
                  onClick={() => social("google")}
                >
                  {msg("Continue with Google")}</button>
              )}
              {config.features.appleLogin && (
                <button
                  type="button"
                  className="button secondary full"
                  disabled={busy || !age}
                  onClick={() => social("apple")}
                >
                  {msg("Continue with Apple")}</button>
              )}
            </div>
          )}
          {!emailVisible && config && (
            <button
              type="button"
              className="button secondary full"
              disabled={busy}
              onClick={() => setMethod("email")}
            >
              <Mail size={18} /> {msg("Continue with email")}</button>
          )}
          {!native && method && hasProviders && (
            <button
              type="button"
              className="text-button"
              disabled={busy}
              onClick={() => { submission.cancel(); setMethod(undefined); }}
            >
              {msg("Use another sign-in method")}</button>
          )}
        </form>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
    </Modal>
  );
}
